import type { AuthMeResponse, UserAccessSummary } from '@zrsystem/contracts';

function hasPlatformRole(roles: readonly string[]): boolean {
  return roles.some((role) => role.startsWith('platform.'));
}

/** Maps legacy `/auth/me` (pre-accesses array) into switcher entries. */
export function buildAccessesFromAuthMe(user: AuthMeResponse): UserAccessSummary[] {
  if (user.accesses && user.accesses.length > 0) {
    return user.accesses;
  }

  const accesses: UserAccessSummary[] = [];

  if (user.placeId) {
    accesses.push({
      scopeType: 'PLACE',
      placeId: user.placeId,
      tenantId: user.tenantId ?? null,
      label: 'Unidade principal',
    });
  }

  if (user.tenantId) {
    accesses.push({
      scopeType: 'TENANT',
      tenantId: user.tenantId,
      label: 'Organização',
    });
  }

  if (user.organizationGroupId) {
    accesses.push({
      scopeType: 'ORGANIZATION_GROUP',
      organizationGroupId: user.organizationGroupId,
      label: 'Rede',
    });
  }

  if (hasPlatformRole(user.roles)) {
    accesses.push({
      scopeType: 'SYSTEM',
      label: 'Plataforma',
      subtitle: 'Entidades e acessos',
    });
  }

  return accesses;
}

export function resolveActiveContextFromAuthMe(user: AuthMeResponse): UserAccessSummary | null {
  if (user.activeContext) {
    return {
      scopeType: user.activeContext.scopeType,
      organizationGroupId: user.activeContext.organizationGroupId,
      tenantId: user.activeContext.tenantId,
      placeId: user.activeContext.placeId,
    };
  }

  const accesses = buildAccessesFromAuthMe(user);
  if (accesses.length === 0) {
    return null;
  }

  const placeAccess = accesses.find((access) => access.scopeType === 'PLACE');
  if (placeAccess) {
    return placeAccess;
  }

  const tenantAccess = accesses.find((access) => access.scopeType === 'TENANT');
  if (tenantAccess) {
    return tenantAccess;
  }

  const networkAccess = accesses.find((access) => access.scopeType === 'ORGANIZATION_GROUP');
  if (networkAccess) {
    return networkAccess;
  }

  return accesses.find((access) => access.scopeType === 'SYSTEM') ?? accesses[0] ?? null;
}
